import React, { useState } from "react";
import { Loader2, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";

function PrefixInput({ label, placeholder, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="label-tiny mb-1">{label}</div>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 text-xs mono outline-none" />
    </label>
  );
}

/** Empty state: API key + optional subaccount / prefixes → connect. `onConnect(body)` resolves true on success. */
export function SparkPostConnectForm({ domain, busy, onConnect, setErr }) {
  const [apiKey, setApiKey] = useState("");
  const [subaccount, setSubaccount] = useState("");
  const [bouncePfx, setBouncePfx] = useState("");
  const [trackingPfx, setTrackingPfx] = useState("");

  async function connect() {
    if (!apiKey.trim()) { setErr("API key required"); return; }
    const body = { api_key: apiKey.trim() };
    if (subaccount.trim()) body.subaccount_id = parseInt(subaccount.trim(), 10);
    if (bouncePfx.trim()) body.bounce_prefix = bouncePfx.trim();
    if (trackingPfx.trim()) body.tracking_prefix = trackingPfx.trim();
    if (await onConnect(body)) { setApiKey(""); setSubaccount(""); setBouncePfx(""); setTrackingPfx(""); }
  }

  return (
    <>
      <div className="text-sm text-zinc-400 leading-relaxed">
        Connect <span className="mono text-zinc-200">{domain}</span> to SparkPost. We&apos;ll create the sending and bounce domains (the bounce domain doubles as the Return-Path) plus a tracking domain in SparkPost and write <strong>4 DNS records</strong> (SPF, DKIM, bounce CNAME, tracking CNAME) into Cloudflare automatically — then trigger verification. No manual DNS work.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="block">
          <div className="label-tiny mb-1.5">SparkPost API key</div>
          <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-sm">
            <KeyRound size={14} className="text-zinc-500 mx-2.5" />
            <input data-testid="sparkpost-api-key-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="paste your SparkPost API key" className="bg-transparent flex-1 px-2 py-2 text-sm mono outline-none" />
          </div>
        </label>
        <label className="block">
          <div className="label-tiny mb-1.5">Subaccount ID (optional)</div>
          <input data-testid="sparkpost-subaccount-input" value={subaccount} onChange={(e) => setSubaccount(e.target.value)} placeholder="leave empty for primary" className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm mono outline-none" />
        </label>
      </div>
      <details className="text-xs text-zinc-400">
        <summary className="cursor-pointer hover:text-zinc-200">Advanced · custom subdomain prefixes</summary>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <PrefixInput label="Bounce / Return-path" placeholder="bounce" value={bouncePfx} onChange={setBouncePfx} testid="sparkpost-bounce-prefix" />
          <PrefixInput label="Tracking" placeholder="click" value={trackingPfx} onChange={setTrackingPfx} testid="sparkpost-tracking-prefix" />
        </div>
        <p className="mt-2 opacity-70">These become the sub-labels of your domain. Leave blank to use SparkPost's defaults.</p>
      </details>
      <button data-testid="sparkpost-connect-btn" onClick={connect} disabled={busy || !apiKey.trim()} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Connect &amp; auto-configure DNS
      </button>
    </>
  );
}

export function NoDomainNotice() {
  return (
    <div data-testid="sparkpost-no-domain" className="border border-amber-500/30 bg-amber-500/5 text-amber-300 rounded-sm p-4 text-sm">
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold mb-1">No domain yet</div>
          <div className="text-xs leading-relaxed opacity-90">Purchase or attach a domain on the Domain tab before connecting SparkPost.</div>
        </div>
      </div>
    </div>
  );
}

export function NoZoneNotice() {
  return (
    <div data-testid="sparkpost-no-cf-zone" className="border border-amber-500/30 bg-amber-500/5 text-amber-300 rounded-sm p-4 text-sm">
      SparkPost integration writes SPF, DKIM, bounce/return-path, and tracking records into Cloudflare — set up the Cloudflare zone first.
    </div>
  );
}
